import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { X, InfoIcon } from "lucide-react";
import {
  useEntityTypes,
  useTerritoryJson,
  useConfigCode,
  usePostConfigMap,
} from "../../../hooks";
import { useDebounce } from "@shared/hooks/useDebounce";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";

interface AddEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: string;
  onSubmit?: (data: any) => void;
}

const AddEntityModal: React.FC<AddEntityModalProps> = ({
  isOpen,
  onClose,
  entityType,
  onSubmit,
}) => {
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [selectedEntityType, setSelectedEntityType] = useState(entityType);
  const [entityName, setEntityName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const utilityId = useRemoteUtilityId();

  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("Service Area Entity");

  useEffect(() => {
    if (isOpen) {
      startTracking();
    }
  }, [startTracking]);
  // Debounce entity name for API call
  const debouncedEntityName = useDebounce(entityName, 500);

  // Fetch entity types
  const { data: entityTypesData } = useEntityTypes();

  // Post mutation hook
  const postConfigMapMutation = usePostConfigMap();

  // Get parent entity level based on selected entity type
  const getParentConfigLevel = (entityTypeValue: string) => {
    const entityTypeMap: Record<string, string> = {
      country: "region",
      state: "country",
      city_county: "state",
      zone: "county",
      division: "zone",
      area: "division",
      sub_area: "area",
      premise: "sub_area",
    };
    return entityTypeMap[entityTypeValue];
  };

  const parentConfigLevel = getParentConfigLevel(selectedEntityType);

  // Fetch parent options based on selected entity type (only when parentConfigLevel exists)
  const { data: parentOptionsData } = useTerritoryJson(parentConfigLevel || "");

  // Fetch config code when entity name changes (only when name has meaningful length)
  const { data: configCodeData } = useConfigCode(debouncedEntityName);

  // Convert entity types to options
  const entityTypeOptions = useMemo(() => {
    if (!entityTypesData?.result) return [];
    return entityTypesData.result.map((type) => ({
      label: type.value,
      value: type.value.toLowerCase().replace(/[\/\s]/g, "_"),
    }));
  }, [entityTypesData]);

  // Convert parent options
  const parentOptions = useMemo(() => {
    if (!parentOptionsData?.result) return [];
    return parentOptionsData.result.map((item) => ({
      label: item.name,
      value: item.code,
    }));
  }, [parentOptionsData]);

  // Get entity title
  const getEntityTitle = (type: string) => {
    if (!entityTypesData?.result) return "Entity";
    const entity = entityTypesData.result.find(
      (e) => e.value.toLowerCase().replace(/[\/\s]/g, "_") === type
    );
    return entity?.value || "Entity";
  };

  // Get entity ID
  const getEntityId = (type: string) => {
    if (!entityTypesData?.result) return 1;
    const entity = entityTypesData.result.find(
      (e) => e.value.toLowerCase().replace(/[\/\s]/g, "_") === type
    );
    return entity?.key || 1;
  };

  const getFieldDescription = (field: string, entityType: string) => {
    const descriptions: Record<string, Record<string, string>> = {
      name: {
        region:
          "Enter a unique name for the region (e.g., North Region, West Coast)",
        country: "Official country name as per government records",
        state: "State or province name within the selected country",
        city_county: "City, county, or municipal area name",
        zone: "Service zone identifier within the city/county",
        division: "Operational division name for management purposes",
        area: "Service area designation for field operations",
        sub_area: "Specific sub-area within the parent area",
        premise: "Building or property identifier (address, name, or code)",
      },
      code: {
        region: "Auto-generated unique code based on region name",
        country: "Auto-generated unique code based on country name",
        state: "Auto-generated unique code based on state name",
        city_county: "Auto-generated unique code based on city name",
        zone: "Auto-generated unique code based on zone name",
        division: "Auto-generated unique code based on division name",
        area: "Auto-generated unique code based on area name",
        sub_area: "Auto-generated unique code based on sub-area name",
        premise: "Auto-generated unique code based on premise name",
      },
      parent: {
        country:
          "Select the region this country belongs to for organizational hierarchy",
        state: "Choose the country this state/province is located in",
        city_county: "Select the state/province this city is part of",
        zone: "Choose the city/county this zone serves",
        division: "Select the zone this division operates under",
        area: "Choose the division this area is assigned to",
        sub_area: "Select the parent area this sub-area is part of",
        premise: "Choose the sub-area where this premise is located",
      },
      description: {
        region:
          "Describe the geographical coverage and key characteristics of this region",
        country:
          "Add details about service coverage, regulations, or special notes",
        state: "Include population served, key cities, or operational notes",
        city_county:
          "Describe urban/rural classification, population, and service characteristics",
        zone: "Define service boundaries, customer types, and operational details",
        division: "Specify management responsibilities and service scope",
        area: "Detail field operation boundaries and service delivery areas",
        sub_area:
          "Describe specific locality characteristics and service requirements",
        premise:
          "Add building details, access information, and special requirements",
      },
      unit: {
        premise:
          "Enter the specific unit number, apartment number, or suite identifier within the premise",
      },
    };
    return (
      descriptions[field]?.[entityType] ||
      "Provide relevant details for this field"
    );
  };

  // Generate form fields dynamically
  const generateFormFields = useCallback((): FormField[] => {
    const currentEntityTitle = getEntityTitle(selectedEntityType);

    const fields: FormField[] = [
      // Entity Type
      {
        name: "entityType",
        type: "select",
        label: "Entity Type",
        required: true,
        placeholder: "Select entity type",
        defaultValue: selectedEntityType,
        options: entityTypeOptions,
        helperText:
          "Choose the type of service entity you want to create in the hierarchy",
        classes: {
          container: "w-full mb-4 col-span-2",
          label: "block text-sm font-medium text-gray-700 mb-1",
          select:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white cursor-pointer",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },

      // Name
      {
        name: "name",
        type: "text",
        label: `${currentEntityTitle} Name`,
        required: true,
        placeholder: `Enter ${currentEntityTitle.toLowerCase()} name`,
        helperText: getFieldDescription("name", selectedEntityType),
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-1",
          input:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },

      // Code (always disabled)
      {
        name: "code",
        type: "text",
        label: `${currentEntityTitle} Code`,
        required: true,
        placeholder: `Auto-generated ${currentEntityTitle.toLowerCase()} code`,
        helperText:
          "This code is automatically generated based on the entity name",
        disabled: true,
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-1",
          input:
            "w-full h-10 px-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-600",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },

      // Parent Entity (show for all except region)
      {
        name: "parent",
        type: "select",
        label: "Parent Entity",
        required: false,
        placeholder:
          parentOptions.length > 0
            ? "Select parent entity"
            : "Loading parent entities...",
        options: parentOptions,
        helperText: getFieldDescription("parent", selectedEntityType),
        disabled: parentOptions.length === 0,
        showWhen: (formData: any) => {
          const currentEntityType = formData.entityType || selectedEntityType;
          return currentEntityType !== "region" && currentEntityType !== "";
        },
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-1",
          select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${
            parentOptions.length === 0
              ? "bg-gray-100 cursor-not-allowed"
              : "bg-white cursor-pointer"
          }`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },

      // Unit (only for premises)
      {
        name: "unit",
        type: "text",
        label: "Unit Number",
        required: false,
        placeholder: "Enter unit number (e.g., 101, A-23, Suite 500)",
        helperText: getFieldDescription("unit", selectedEntityType),
        showWhen: (formData: any) => {
          const currentEntityType = formData.entityType || selectedEntityType;
          return currentEntityType === "premise";
        },
        classes: {
          container: "w-full mb-4 col-span-2",
          label: "block text-sm font-medium text-gray-700 mb-1",
          input:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },

      // Description
      {
        name: "description",
        type: "textarea",
        label: "Description",
        required: false,
        placeholder: `Enter ${currentEntityTitle.toLowerCase()} description`,
        helperText: getFieldDescription("description", selectedEntityType),
        classes: {
          container: "w-full mb-4 col-span-2",
          label: "block text-sm font-medium text-gray-700 mb-1",
          textarea:
            "w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white resize-none",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },

      // Status
      {
        name: "is_active",
        type: "radio",
        label: "Status",
        required: true,
        defaultValue: "true",
        options: [
          { label: "Active", value: "true" },
          { label: "Inactive", value: "false" },
        ],
        helperText:
          "Active entities are included in operations and billing processes",
        classes: {
          container: "w-full mb-4 col-span-2",
          label: "block text-sm font-medium text-gray-700 mb-2",
          radio: "text-blue-600 focus:ring-blue-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
    ];

    return fields;
  }, [selectedEntityType, entityTypeOptions, parentOptions]);

  // Default values
  const defaultValues = useMemo(
    () => ({
      entityType: selectedEntityType,
      name: "",
      code: "",
      parent: "",
      description: "",
      is_active: "true",
      unit: "",
    }),
    [selectedEntityType]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: generateFormFields(),
    defaultValues,
    mode: "onChange",
    validateOnMount: false,
  });

  // Remove the form watch useEffect completely to avoid loops

  // Set auto-generated code when API responds
  useEffect(() => {
    if (configCodeData && entityName.length > 2) {
      const generatedCode = configCodeData?.configurationCode;

      if (typeof generatedCode === "string" && generatedCode.trim()) {
        form.setValue("code", generatedCode);
      }
    }
  }, [configCodeData, form, entityName]);

  // Handle tag operations
  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  // Handle field changes - centralized logic to avoid loops
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      console.log(`Field ${fieldName} changed to:`, value);

      if (fieldName === "name") {
        setEntityName(value || "");
      }

      if (fieldName === "entityType") {
        const newEntityType = value || "";
        setSelectedEntityType(newEntityType);
        // Reset dependent fields when entity type changes
        setTimeout(() => {
          form.setValue("parent", "");
          form.setValue("code", "");
          setEntityName("");
        }, 0);
      }

      if (FormService.getFieldError(form, fieldName)) {
        form.clearErrors(fieldName);
      }
    },
    [form]
  );

  // Reset form completely
  const resetForm = useCallback(() => {
    form.reset();
    setTags([]);
    setNewTag("");
    setEntityName("");
    setSelectedEntityType(entityType);
    setIsSubmitting(false);
  }, [form, entityType]);

  // Handle form submission

  const handleSubmit = async (formData: any) => {
    try {
      setIsSubmitting(true);
      console.log("Raw form data:", formData);

      const entityType = formData.entityType;

      const payload: any = {
        mapType: "TERRITORY_JSON",
        remote_utility_id: utilityId,
        entity_type: getEntityId(entityType),
        name: formData.name,
        configuration_code: formData.code,
        description: formData.description || "",
        is_active: formData.is_active === "true",
        tags: tags,
      };

      // ✅ Special handling for region
      if (entityType === "region") {
        payload.code = formData.name;
      } else if (formData.parent) {
        payload.code = `${formData.parent}_${formData.name}`;
      }

      if (formData.unit) {
        payload.total_units = formData.unit;
      }

      console.log("Final payload:", payload);

      await postConfigMapMutation.mutateAsync(payload, {
        onSuccess: () => {
          stopTracking();
        },
      });

      if (onSubmit) {
        onSubmit(payload);
      }

      resetForm();
      onClose();
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle close with reset
  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  // Update form when entityType prop changes - only on mount
  useEffect(() => {
    if (entityType && isOpen) {
      setSelectedEntityType(entityType);
      form.setValue("entityType", entityType);
    }
  }, [entityType, isOpen, form]);

  // Custom footer with tags and actions
  const renderCustomFooter = useCallback(
    () => (
      <div className="space-y-4">
        {/* Tags Section */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Tags
            <InfoIcon className="inline w-4 h-4 text-gray-400 ml-1" />
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="Add tag"
              className="flex-1 h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white"
              onKeyPress={(e) =>
                e.key === "Enter" && (e.preventDefault(), handleAddTag())
              }
              disabled={isSubmitting}
            />
            <Button
              type="button"
              onClick={handleAddTag}
              variant="outline"
              disabled={isSubmitting}
            >
              Add
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Add tags for easy searching and categorization (e.g., high-priority,
            rural, industrial)
          </p>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="cursor-pointer">
                  {tag}
                  <X
                    className="w-3 h-3 ml-1"
                    onClick={() => !isSubmitting && removeTag(tag)}
                  />
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Creating..."
              : `Create ${getEntityTitle(selectedEntityType)}`}
          </Button>
        </div>
      </div>
    ),
    [tags, newTag, selectedEntityType, handleClose, isSubmitting]
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Add New {getEntityTitle(selectedEntityType)}
          </DialogTitle>
        </DialogHeader>

        <DynamicForm
          fields={generateFormFields()}
          form={form}
          onSubmit={handleSubmit}
          onReset={resetForm}
          onFieldChange={handleFieldChange}
          gridColumns={2}
          responsiveColumns={{
            sm: 1,
            md: 2,
          }}
          config={{
            showProgress: false,
            layout: "vertical",
            spacing: "comfortable",
            submitButtonText: isSubmitting
              ? "Creating..."
              : `Create ${getEntityTitle(selectedEntityType)}`,
            showResetButton: false,
            resetOnSubmit: false,
            classes: {
              form: "space-y-0",
              fieldContainer: "",
              buttonContainer: "hidden",
              fieldGroup: "",
            },
          }}
          renderCustomFooter={renderCustomFooter}
          className="space-y-6"
        />
      </DialogContent>
    </Dialog>
  );
};

export default AddEntityModal;
